import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api, type FbsPostingItem, type ShipmentRow } from '../api';

const STATUSES = [
  { value: 'awaiting_packaging', label: 'Ожидают сборки' },
  { value: 'awaiting_deliver', label: 'Ожидают отгрузки' },
  { value: 'delivering', label: 'В доставке' },
  { value: 'delivered', label: 'Доставлены' },
  { value: 'cancelled', label: 'Отменены' },
] as const;

export function ShipmentsPage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]['value']>('awaiting_packaging');
  const postings = useQuery({
    queryKey: ['fbs-postings', status],
    queryFn: () => api.fbsPostings(status),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="page-header">
        <h2>Отгрузки</h2>
        <select className="input" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {postings.isLoading && <div className="card">Загрузка…</div>}
      {postings.error && <div className="card" style={{ color: 'var(--red)' }}>{(postings.error as Error).message}</div>}

      {postings.data && (
        <>
          <div className="muted" style={{ marginBottom: 8 }}>Найдено: {postings.data.total}</div>
          {postings.data.items.length === 0 ? (
            <div className="card muted" style={{ textAlign: 'center', padding: 32 }}>Нет отправлений с этим статусом.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {postings.data.items.map((p) => <PostingCard key={p.posting_number} p={p} />)}
            </div>
          )}
        </>
      )}

      <HistoryBlock />
    </>
  );
}

function PostingCard({ p }: { p: FbsPostingItem }) {
  const needsMark = p.products.some((pr) => pr.mandatory_mark_required);
  const totalItems = p.products.reduce((s, pr) => s + pr.quantity, 0);
  const deadline = p.shipment_date ? new Date(p.shipment_date) : null;
  const hoursLeft = deadline ? Math.round((deadline.getTime() - Date.now()) / 3600_000) : null;

  return (
    <Link to={`/shipments/${p.posting_number}`} className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{p.posting_number}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{p.delivery_method_name ?? 'без метода'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {needsMark && <span style={{ background: '#fff7ed', color: '#c2410c', fontSize: 11, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>МАРКИРОВКА</span>}
          {hoursLeft != null && <div style={{ fontSize: 12, marginTop: 4, color: hoursLeft < 4 ? 'var(--red)' : 'var(--text-muted)' }}>⏰ {hoursLeft > 0 ? `${hoursLeft}ч` : 'просрочено'}</div>}
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 13 }}>
        {totalItems} ед. в {p.products.length} строк{p.products.length === 1 ? 'е' : 'ах'}
        {p.products.slice(0, 2).map((pr) => (
          <div key={pr.product_id} className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            • {pr.name} × {pr.quantity} ({pr.offer_id}){pr.mandatory_mark_required && ' ⚠'}
          </div>
        ))}
        {p.products.length > 2 && <div className="muted" style={{ fontSize: 12 }}>…и ещё {p.products.length - 2}</div>}
      </div>
    </Link>
  );
}

function HistoryBlock() {
  const qc = useQueryClient();
  const ships = useQuery({ queryKey: ['shipments-history'], queryFn: () => api.fbsShipments(20), refetchInterval: 30_000 });
  const resync = useMutation({
    mutationFn: (id: string) => api.fbsResyncOneC(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments-history'] }),
  });

  return (
    <div style={{ marginTop: 32 }}>
      <h3>Последние отгрузки</h3>
      <table className="products">
        <thead>
          <tr><th>№</th><th>Когда</th><th>Доставка</th><th className="num">Марок</th><th>1С</th><th></th></tr>
        </thead>
        <tbody>
          {ships.data?.items.length === 0 && <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 16 }}>Истории пока нет.</td></tr>}
          {ships.data?.items.map((s) => <ShipmentHistoryRow key={s.id} s={s} onResync={() => resync.mutate(s.id)} resyncing={resync.isPending} />)}
        </tbody>
      </table>
    </div>
  );
}

function ShipmentHistoryRow({ s, onResync, resyncing }: { s: ShipmentRow; onResync: () => void; resyncing: boolean }) {
  return (
    <tr>
      <td>{s.posting_number}</td>
      <td className="muted">{s.shipped_at ? new Date(s.shipped_at).toLocaleString('ru-RU') : '—'}</td>
      <td className="muted">{s.delivery_method ?? '—'}</td>
      <td className="num">{s.marks_count}</td>
      <td>
        {s.onec_synced_at && <span style={{ color: 'var(--green)', fontSize: 12 }}>✓ {s.onec_document_id ?? 'ok'}</span>}
        {s.onec_error && <span style={{ color: 'var(--red)', fontSize: 12 }} title={s.onec_error}>⚠ ошибка</span>}
        {!s.onec_synced_at && !s.onec_error && <span className="muted" style={{ fontSize: 12 }}>—</span>}
      </td>
      <td>
        {(s.onec_error || !s.onec_synced_at) && <button className="btn" onClick={onResync} disabled={resyncing}>↻ 1С</button>}
      </td>
    </tr>
  );
}
